/**
 * useSessionCreate hook - Manages session creation flow.
 *
 * Provides state management for:
 * - Session type selection
 * - Session configuration
 * - Consent handling
 * - API integration for session creation
 */

import { useState, useCallback } from 'react'
import { getApiClient, type CreateSessionRequest, type CreateSessionResponse } from '@/services/api'

/**
 * Session creation flow step
 */
export type SessionCreationStep = 'start' | 'config' | 'consent' | 'creating' | 'complete' | 'error'

/**
 * Session configuration data
 */
import type { AgeRange } from '@/components/session/SessionConfig'

export interface SessionConfig {
  sessionType: 'individual' | 'group' | 'ambient'
  participantCount: number
  location: string
  facilitatorId: string
  facilitatorName?: string
  postcode?: string
  ageRanges?: AgeRange[]
}

/**
 * Session creation state
 */
export interface SessionCreateState {
  step: SessionCreationStep
  sessionType: 'individual' | 'group' | 'ambient' | null
  config: SessionConfig | null
  sessionId: string | null
  isLoading: boolean
  error: string | null
}

/**
 * Session creation actions
 */
export interface SessionCreateActions {
  setSessionType: (type: 'individual' | 'group' | 'ambient') => void
  setConfig: (config: SessionConfig) => void
  createSession: (consentGiven: boolean, consentText: string, participantId: string, eventContext?: { eventId: string; groundingImageIds: string[] }) => Promise<CreateSessionResponse>
  reset: () => void
  goBack: () => void
}

/**
 * Session creation hook return type
 */
export interface UseSessionCreateReturn extends SessionCreateState, SessionCreateActions {}

/**
 * Default consent text shown to participants
 */
export const DEFAULT_CONSENT_TEXT = `
By participating in this Community Futures session, you consent to:

1. **Recording**: Your conversation will be recorded and processed by AI to understand your vision for the future.

2. **Image Generation**: An AI-generated image will be created based on themes from our conversation.

3. **Data Storage**: Your responses and generated images will be stored securely in UK/EU data centres for up to 90 days, then automatically deleted.

4. **Research Use**: Anonymised themes may be used to inform community planning efforts.

5. **Optional Email**: At the end of the session you may choose to share your email address so we can send you your generated image. This is entirely optional. Your email will only be used for this purpose and will be automatically deleted after 90 days.

6. **Your Rights**: You can withdraw at any time. All your data will be deleted immediately upon request.

Your participation is voluntary. You can stop at any point.
`.trim()

/**
 * Hook for managing session creation flow
 */
export function useSessionCreate(): UseSessionCreateReturn {
  const [state, setState] = useState<SessionCreateState>({
    step: 'start',
    sessionType: null,
    config: null,
    sessionId: null,
    isLoading: false,
    error: null,
  })

  const apiClient = getApiClient()

  /**
   * Set session type and advance to config step
   */
  const setSessionType = useCallback((type: 'individual' | 'group' | 'ambient') => {
    setState((prev) => ({
      ...prev,
      sessionType: type,
      step: 'config',
      error: null,
    }))
  }, [])

  /**
   * Set session config and advance to consent step
   */
  const setConfig = useCallback((config: SessionConfig) => {
    setState((prev) => ({
      ...prev,
      config,
      step: 'consent',
      error: null,
    }))
  }, [])

  /**
   * Create session with API
   */
  const createSession = useCallback(
    async (
      consentGiven: boolean,
      consentText: string,
      participantId: string,
      eventContext?: { eventId: string; groundingImageIds: string[] }
    ): Promise<CreateSessionResponse> => {
      if (!state.config) {
        throw new Error('Session configuration is required')
      }

      setState((prev) => ({
        ...prev,
        step: 'creating',
        isLoading: true,
        error: null,
      }))

      try {
        const request: CreateSessionRequest = {
          session_type: state.config.sessionType,
          participant_count: state.config.participantCount,
          facilitator_id: state.config.facilitatorId,
          facilitator_name: state.config.facilitatorName,
          location: state.config.location,
          consent_text_shown: consentText,
          consent_given: consentGiven,
          participant_id: participantId,
          postcode: state.config.postcode || undefined,
          age_ranges: state.config.ageRanges?.length ? state.config.ageRanges : undefined,
          ...(eventContext && {
            event_id: eventContext.eventId,
            grounding_image_ids: eventContext.groundingImageIds,
          }),
        }

        const response = await apiClient.createSession(request)

        setState((prev) => ({
          ...prev,
          sessionId: response.session_id,
          step: 'complete',
          isLoading: false,
        }))

        return response
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create session'

        setState((prev) => ({
          ...prev,
          step: 'error',
          isLoading: false,
          error: errorMessage,
        }))

        throw error
      }
    },
    [state.config, apiClient]
  )

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    setState({
      step: 'start',
      sessionType: null,
      config: null,
      sessionId: null,
      isLoading: false,
      error: null,
    })
  }, [])

  /**
   * Go back one step
   */
  const goBack = useCallback(() => {
    setState((prev) => {
      switch (prev.step) {
        case 'config':
          return { ...prev, step: 'start', error: null }
        case 'consent':
          return { ...prev, step: 'config', error: null }
        case 'error':
          return { ...prev, step: 'consent', error: null }
        default:
          return prev
      }
    })
  }, [])

  return {
    ...state,
    setSessionType,
    setConfig,
    createSession,
    reset,
    goBack,
  }
}

export default useSessionCreate
